import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import { Link, useNavigate } from 'react-router-dom';
import AccountApiService from '../../api/AccountApiService';
import PageShell from '../PageShell';
import PayBalanceModal from '../Modal/StandardFunctions/PayBalanceModal';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import * as Constants from '../../utilities/Constants';

/**
 * Every charge and payment on a tenant's account, newest first.
 *
 * The point of the page is to make a balance defensible. Before the ledger, a tenant told they owed
 * money had no way to see what it was for and the owner had no way to show them beyond reading out
 * card attempts; now the figure has a line for each month behind it, with the running balance beside
 * it so the total can be followed down the page.
 */
export default function AccountStatement(props) {
    const { setSnackbarProperties, isMobile } = props;

    const [statement, setStatement] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPay, setShowPay] = useState(false);

    const accountApiService = new AccountApiService();
    const navigate = useNavigate();

    async function loadStatement() {
        setLoading(true);

        const response = await accountApiService.getMyStatement();

        if (response.success) {
            setStatement(response.data);
        } else {
            setSnackbarProperties(`Unable to load your statement: ${response.message}`, 'error');
        }

        setLoading(false);
    }

    useEffect(() => {
        if (!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);

            return;
        }

        document.title = 'Executive Storage Suites - Your Statement';
        loadStatement();
    }, []);

    const columns = [
        {
            field: 'entryDate',
            headerName: 'Date',
            flex: 0.8,
            renderCell: (params) => new Date(params.row.entryDate).toLocaleDateString(),
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 2.2,
        },
        {
            field: 'storageUnitName',
            headerName: 'Unit',
            flex: 0.6,

            // Blank rather than "N/A": an account-level credit genuinely relates to no unit, and a
            // column of N/A reads as missing data.
            renderCell: (params) => params.row.storageUnitName ?? '',
        },
        {
            field: 'amount',
            headerName: 'Amount',
            flex: 0.8,
            renderCell: (params) => (
                <Typography
                    variant={'body2'}
                    sx={{ color: params.row.amount < 0 ? 'success.main' : 'text.primary' }}
                >
                    {Constants.USDollar.format(params.row.amount)}
                </Typography>
            ),
        },
        {
            field: 'runningBalance',
            headerName: 'Balance',
            flex: 0.8,
            renderCell: (params) => Constants.USDollar.format(params.row.runningBalance),
        },
    ];

    // Billed outside the application. Their ledger is empty by construction, so the whole page -
    // summary, figures and grid - would be an elaborate way of showing a zero that means nothing.
    const billedDirectly = statement && !statement.balanceIsTracked && (
        <Card variant={'outlined'}>
            <CardContent>
                <Typography variant={'h6'} sx={{ fontWeight: 700 }}>
                    Your account is billed directly
                </Typography>
                <Typography variant={'body2'} sx={{ color: 'text.secondary', marginTop: '8px' }}>
                    Your rent is arranged with the facility rather than charged to a card here, so
                    your charges and payments are not recorded on this page. Please contact us for a
                    statement or to make a payment.
                </Typography>
                <Button
                    component={Link}
                    to={'/contact'}
                    variant={'contained'}
                    sx={{ marginTop: '16px' }}
                >
                    Contact us
                </Button>
            </CardContent>
        </Card>
    );

    const summary = statement && (
        <Card variant={'outlined'} sx={{ marginBottom: '16px' }}>
            <CardContent>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 2 : 4}
                    divider={!isMobile && <Divider orientation={'vertical'} flexItem />}
                    justifyContent={'space-between'}
                    alignItems={isMobile ? 'stretch' : 'center'}
                >
                    <Box>
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            Current balance
                        </Typography>
                        <Typography
                            variant={'h4'}
                            sx={{
                                fontWeight: 700,
                                color: statement.isPastDue ? 'error.main' : 'text.primary',
                            }}
                        >
                            {Constants.USDollar.format(statement.balance)}
                        </Typography>
                        {statement.balance < 0 &&
                            <Typography variant={'body2'} sx={{ color: 'success.main' }}>
                                Your account is in credit.
                            </Typography>
                        }
                    </Box>

                    <Box>
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            Overdue
                        </Typography>
                        <Typography variant={'h6'}>
                            {Constants.USDollar.format(statement.pastDue)}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            Due this month
                        </Typography>
                        <Typography variant={'h6'}>
                            {Constants.USDollar.format(statement.currentDue)}
                        </Typography>
                    </Box>

                    {statement.hasBalance && statement.canPayOnline &&
                        <Button
                            variant={'contained'}
                            color={statement.isPastDue ? 'error' : 'primary'}
                            onClick={() => setShowPay(true)}
                            sx={{ flexShrink: 0 }}
                        >
                            {`Pay ${Constants.USDollar.format(statement.balance)}`}
                        </Button>
                    }
                </Stack>
            </CardContent>
        </Card>
    );

    const entries = statement?.entries ?? [];

    // A tenant billed directly has no entries and never will, so counting them would read as an
    // empty statement rather than as a statement that is kept somewhere else.
    const subtitle = loading
        ? 'Loading...'
        : billedDirectly
            ? 'Arranged with the facility'
            : `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`;

    if (!loading && billedDirectly) {
        return (
            <PageShell title={'Your Statement'} subtitle={subtitle} isMobile={isMobile} disableCard={true}>
                {billedDirectly}
            </PageShell>
        );
    }

    return (
        <PageShell
            title={'Your Statement'}
            subtitle={subtitle}
            isMobile={isMobile}
            disableCard={true}
        >
            {loading
                ? <Backdrop open={loading}><CircularProgress color={'inherit'} /></Backdrop>
                : <>
                    {summary}

                    {entries.length === 0
                        ? <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            There is nothing on your account yet. Charges and payments will appear here.
                        </Typography>
                        : isMobile
                            // The grid does not survive a phone width with five columns, and a
                            // statement is read line by line rather than sorted and filtered.
                            ? <Stack spacing={1}>
                                {entries.map((entry) =>
                                    <Card key={entry.id} variant={'outlined'}>
                                        <CardContent sx={{ paddingBottom: '16px !important' }}>
                                            <Stack direction={'row'} justifyContent={'space-between'}>
                                                <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                                                    {new Date(entry.entryDate).toLocaleDateString()}
                                                </Typography>
                                                <Typography
                                                    variant={'body2'}
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: entry.amount < 0 ? 'success.main' : 'text.primary',
                                                    }}
                                                >
                                                    {Constants.USDollar.format(entry.amount)}
                                                </Typography>
                                            </Stack>
                                            <Typography variant={'body1'}>{entry.description}</Typography>
                                            <Typography variant={'caption'} sx={{ color: 'text.secondary' }}>
                                                {`Balance after: ${Constants.USDollar.format(entry.runningBalance)}`}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                            : <Box sx={{ height: '60vh' }}>
                                <DataGrid
                                    rows={entries}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    getRowHeight={() => 'auto'}
                                    disableRowSelectionOnClick
                                />
                            </Box>
                    }
                </>
            }

            <PayBalanceModal
                open={showPay}
                close={() => setShowPay(false)}
                statement={statement}
                userId={authenticationManager.getUserId()}
                isMobile={isMobile}
                setSnackbarProperties={setSnackbarProperties}
                onPaid={loadStatement}
            />
        </PageShell>
    );
}

AccountStatement.propTypes = {
    setSnackbarProperties: PropTypes.func,
    isMobile: PropTypes.bool,
};
